import React from 'react';
import {
  Box, Card, CardContent, Typography, Button, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip, useMediaQuery, useTheme
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon, Check as CheckIcon, Close as CloseIcon, 
  Person as PersonIcon, AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const WithdrawalApprovals = ({ transactions, onApprovalUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const approveMutation = useMutation(
    async ({ transactionId, action, comment }) => {
      const response = await apiClient.post(`/api/joint-transactions/${transactionId}/approve`, {
        action,
        comment
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('jointAccounts');
        queryClient.invalidateQueries('pendingApprovals');
        if (onApprovalUpdate) onApprovalUpdate();
      }
    }
  );

  const handleApprove = (transactionId) => {
    approveMutation.mutate({ transactionId, action: 'approved' });
  };

  const handleReject = (transactionId) => {
    approveMutation.mutate({ transactionId, action: 'rejected' });
  };

  if (!transactions || transactions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
             <Typography variant="h6" sx={{ 
         color: '#FFFFFF', 
         mb: 2,
         display: 'flex',
         alignItems: 'center',
         gap: 1
       }}>
         <MoneyIcon sx={{ color: '#F7931A' }} />
         Pending Approvals ({transactions.length})
       </Typography>
       
       <Typography variant="body2" sx={{ color: '#6C7293', mb: 2 }}>
         Review and approve withdrawal and transfer requests. After all members approve, transactions will be sent for final admin approval.
       </Typography>

      <List sx={{ width: '100%' }}>
        {transactions.map((transaction) => {
          const hasApproved = transaction.hasUserApproved;
          const hasRejected = transaction.hasUserRejected;
          const canApprove = transaction.canUserApprove;
          
          return (
            <Card
              key={transaction.id}
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: '2px solid #F7931A',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#FFB800',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 20px rgba(247, 147, 26, 0.3)'
                }
              }}
            >
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#F7931A', width: 56, height: 56 }}>
                    <MoneyIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        {transaction.transaction_type === 'transfer' ? 'Transfer Request' : 'Withdrawal Request'}
                      </Typography>
                      <Chip label="Pending Approval" color="warning" size="small" />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Amount: ${transaction.amount}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Requested by: {transaction.initiator?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Joint Account: {transaction.jointAccount?.account_name}
                      </Typography>
                      {transaction.description && (
                        <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                          Reason: {transaction.description}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: '#F7931A', fontWeight: 600 }}>
                        Reference: {transaction.reference}
                      </Typography>
                      
                      {/* Show Approval Details */}
                      {transaction.approvals && transaction.approvals.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: '#6C7293', fontWeight: 600 }}>
                            Member Approvals ({transaction.approvals.length}):
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {transaction.approvals.map((approval, idx) => (
                              <Chip
                                key={idx}
                                label={`${approval.approver?.name}: ${approval.action}`}
                                color={approval.action === 'approved' ? 'success' : 'error'}
                                size="small"
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Show Approval Progress */}
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#6C7293' }}>
                          Required: {transaction.jointAccount?.active_members_count || 0} members • 
                          Received: {transaction.approvals?.filter(a => a.action === 'approved').length || 0} approvals
                        </Typography>
                      </Box>
                    </Box>
                  }
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                  {hasApproved ? (
                    <Chip label="Approved" color="success" icon={<CheckIcon />} />
                  ) : hasRejected ? (
                    <Chip label="Rejected" color="error" icon={<CloseIcon />} />
                  ) : canApprove ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => handleApprove(transaction.id)}
                        disabled={approveMutation.isLoading}
                        sx={{
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                          }
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() => handleReject(transaction.id)}
                        disabled={approveMutation.isLoading}
                        sx={{
                          borderColor: '#F84960',
                          color: '#F84960',
                          '&:hover': {
                            borderColor: '#DC2626',
                            backgroundColor: 'rgba(248, 73, 96, 0.1)'
                          }
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#6C7293' }}>
                      Cannot approve
                    </Typography>
                  )}
                </Box>
              </ListItem>
            </Card>
          );
        })}
      </List>
    </Box>
  );
};

export default WithdrawalApprovals; 